import { Box, Circle, Flex, Icon, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useUnseenFollowers } from '#modules/user/features/friends/hooks/useUnseenFollowers';
import {
  hasUnreadChatsQueryOptions,
  unreadChatsCountQueryOptions,
} from '#features/chats/model/chats';
import { locales } from '#features/i18n';
import { Link } from '#features/routing';
import { AddIcon, UnreadBadge } from '#features/ui';
import { floatingBarSurface } from '#features/ui/floatingBarSurface';
import { pressFeedback } from '#features/ui/theme/pressFeedback';
import { useHeightVar } from '#hooks/useHeightVar';
import { useGuardedLink } from '../../hooks/useGuardedLink';
import { useNavigationItems } from '../useNavigationItems';

const localePattern = new RegExp(`^/(${Object.keys(locales).join('|')})(?=/|$)`);
const stripLocale = (value: string) => value.replace(localePattern, '');
const stripLocaleToken = (value: string) => value.replace('/{-$locale}', '');

type BottomNavigationProps = {
  /**
   * Открыта клавиатура: панель убирается целиком, но не размонтируется – счётчики и измерение
   * высоты остаются на месте, и после набора она возвращается без повторных запросов.
   */
  hidden?: boolean;
};

export const BottomNavigation = ({ hidden = false }: BottomNavigationProps) => {
  const { t } = useLingui();
  const guardLink = useGuardedLink();
  const { pathname } = useLocation();
  const isRouterLoading = useRouterState({ select: (s) => s.isLoading });
  const isAuth = useIsAuth();
  // Гость отсеивается ниже по коду, но хуки выполняются раньше — без `enabled` обе выборки
  // успевают уйти на каждой публичной странице и вернуться ошибкой «the user is not logged in».
  const { data: hasUnreadChats = false } = useQuery({
    ...hasUnreadChatsQueryOptions(),
    enabled: isAuth,
  });
  const { data: unreadChatsCount = 0 } = useQuery({
    ...unreadChatsCountQueryOptions(),
    enabled: isAuth,
  });
  const unseenFollowers = useUnseenFollowers(isAuth);
  const items = useNavigationItems();
  // Высота навигации уезжает в CSS-переменную: по ней окно разговора встаёт над панелью, а не
  // поверх неё, и на страницах без нижнего меню опускается к самому краю экрана.
  const [navNode, setNavNode] = useState<HTMLElement | null>(null);
  useHeightVar('--bottom-nav-height', navNode);
  const pathnameWithoutLocale = stripLocale(pathname);
  const isCreateRoute = pathnameWithoutLocale.startsWith('/dashboard/discover/create');
  if (!isAuth) {
    return null;
  }

  const visibleItems = items.filter(({ isProtected }) => {
    if (isProtected === true) {
      return true;
    }
    if (isProtected === false) {
      return false;
    }
    return true;
  });

  const mobileItems = visibleItems.filter(
    ({ to }) => !(typeof to === 'string' && to.includes('/feedback'))
  );

  const createItem = {
    name: t`dashboard:posts.createShort`,
    to: '/{-$locale}/dashboard/discover/create' as const,
    icon: AddIcon,
    exact: true,
  };

  const mobileItemsWithCreate = (() => {
    const nextItems: Array<(typeof mobileItems)[number] | typeof createItem> = [];
    let inserted = false;

    for (const item of mobileItems) {
      nextItems.push(item);

      if (
        !inserted &&
        typeof item.to === 'string' &&
        item.to === '/{-$locale}/dashboard/discover'
      ) {
        nextItems.push(createItem);
        inserted = true;
      }
    }

    if (!inserted) {
      nextItems.push(createItem);
    }

    return nextItems;
  })();

  if (mobileItemsWithCreate.length === 0) {
    return null;
  }

  return (
    <Box
      // `asChild` вместо `as="nav"`: настоящий <nav> нужен, чтобы взять на себя ref, — Chakra с
      // `as` ref не типизирует вовсе. А ref здесь именно функция: состояние с узлом перезапускает
      // измерение и когда панель появляется, и когда исчезает.
      asChild
      {...floatingBarSurface}
      // Края, скругление и нижний отступ совпадают с полосой комнаты сессии, которая встаёт прямо
      // над панелью (`MOBILE_BAR_BOTTOM`: панель + 16px, из них 8 - отступ самой панели). Так
      // они читаются одним блоком, а не двумя случайными плашками. Раньше от `sm` панель стояла
      // на 20px и шире полосы, и зазор между ними сжимался до 4px.
      display={hidden ? 'none' : { base: 'block', md: 'none' }}
      data-hidden={hidden ? '' : undefined}
      left={3}
      right={3}
      position="fixed"
      bottom="calc(env(safe-area-inset-bottom) + 8px)"
      w={{ base: 'auto', lg: '100%' }}
      zIndex="sticky"
      py={1}
    >
      <nav ref={setNavNode}>
        <Flex
          alignItems="flex-end"
          gap={1}
          justifyContent="space-between"
          maxW="1800px"
          mx="auto"
          px={3}
        >
          {mobileItemsWithCreate.map(({ to, name, icon, exact = true }) => {
            const isDiscoverItem =
              typeof to === 'string' && to === '/{-$locale}/dashboard/discover';
            const isCreateItem =
              typeof to === 'string' && to === '/{-$locale}/dashboard/discover/create';
            const isChatsItem = typeof to === 'string' && to === '/{-$locale}/dashboard/chats';
            const isFriendsItem = typeof to === 'string' && to === '/{-$locale}/dashboard/friends';
            const badgeCount =
              (isChatsItem && hasUnreadChats ? unreadChatsCount : 0) ||
              (isFriendsItem ? unseenFollowers : 0);
            const resolvedTo = typeof to === 'string' ? stripLocaleToken(to) : '';
            const isActive = isCreateItem
              ? isCreateRoute
              : isDiscoverItem
                ? pathnameWithoutLocale.startsWith('/dashboard/discover') && !isCreateRoute
                : resolvedTo.length > 0 &&
                  (exact
                    ? pathnameWithoutLocale === resolvedTo
                    : pathnameWithoutLocale.startsWith(resolvedTo));

            if (isCreateItem) {
              return (
                <Flex
                  key={to}
                  flex="1"
                  justifyContent="center"
                  alignSelf="center"
                  minW="0"
                  {...pressFeedback}
                  focusVisibleRing="outside"
                  borderRadius="full"
                  asChild
                >
                  <Link to={to} aria-label={name} onClick={guardLink}>
                    <Circle
                      size="46px"
                      // Создание - отдельное действие; у него нет состояния активного раздела.
                      bg="purple.solid"
                    >
                      <Icon as={icon} boxSize={6} color="white" />
                    </Circle>
                  </Link>
                </Flex>
              );
            }

            return (
              <Flex
                key={to}
                alignItems="center"
                borderRadius="md"
                bg={isActive ? 'bg.selected' : undefined}
                color="fg.secondary"
                display="flex"
                flex="1"
                justifyContent="center"
                minW="0"
                px={1}
                py={1}
                textAlign="center"
                {...pressFeedback}
                _hover={{ bg: isActive ? 'bg.selected' : 'bg.muted', color: 'fg' }}
                focusVisibleRing="outside"
                asChild
              >
                <Link
                  to={to}
                  onClick={guardLink}
                  // Тот же уговор, что у пункта в меню аватара: пока горит значок новых
                  // подписчиков, ведём сразу на их вкладку. Иначе новичок попадал бы на пустую
                  // вкладку «Друзья», никого не находил, и значок не гас.
                  search={isFriendsItem && unseenFollowers > 0 ? { tab: 'followers' } : undefined}
                  aria-label={name}
                  // Точное совпадение адреса, а не «начинается с»: иначе роутер считает
                  // `/dashboard` активным на каждом вложенном экране, и читалка объявляет две
                  // текущие страницы сразу. Подсветка своя, поэтому активность берётся из
                  // `isActive`, а не из состояния ссылки.
                  activeOptions={{ exact: true, includeSearch: false }}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Flex
                    alignItems="center"
                    color="inherit"
                    flexDirection="column"
                    gap={1}
                    justifyContent="center"
                    minH="36px"
                    px={2}
                    py={0}
                    transition="color var(--chakra-durations-moderate) ease"
                  >
                    <Box
                      position="relative"
                      boxSize="icon.nav"
                      animation={
                        isActive && isRouterLoading ? 'pulse 1s ease-in-out infinite' : undefined
                      }
                    >
                      <Icon as={icon} boxSize="icon.nav" color={isActive ? 'white' : 'fg.icon'} />
                      {badgeCount > 0 ? (
                        <Box position="absolute" top="-4px" right="-8px">
                          <UnreadBadge
                            count={badgeCount}
                            size="xs"
                            ring="bg"
                            // Метка для e2e: сигнал новых подписчиков ищут по ней.
                            data-testid={isFriendsItem ? 'new-followers-badge' : undefined}
                          />
                        </Box>
                      ) : null}
                    </Box>
                    <Text
                      textStyle={isActive ? 'badge' : 'caption'}
                      // Высота строки фиксируется ступенью, а не пикселем: подпись меняет
                      // насыщенность на активном пункте, и без общей высоты ряд подпрыгивал.
                      lineHeight="none"
                      maxW="90px"
                      minH="14px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      color={isActive ? 'white' : 'fg.muted'}
                    >
                      {name}
                    </Text>
                  </Flex>
                </Link>
              </Flex>
            );
          })}
        </Flex>
      </nav>
    </Box>
  );
};
